/**
 * Text functions of the Core_Formula_Set (Requirement 4.1).
 *
 * String positions are 1-based, as in Excel: `MID("abc",1,2)` is `"ab"`, and a
 * start position below 1 is `#VALUE!`. `FIND` is case-sensitive and literal,
 * `SEARCH` is case-insensitive and accepts `*`/`?` wildcards — the pair of
 * behaviours users rely on.
 *
 * `TEXT` implements the number-format subset the Core_Formula_Set needs on its
 * own (digit placeholders, thousands separators, percent, and the common date
 * and time tokens). The Format_Manager owns display formatting for *cells*;
 * keeping `TEXT` self-contained avoids a dependency from the Formula_Engine on
 * the Format_Manager in the initial bundle, and the two agree on the cases they
 * share because both are driven by the same token vocabulary.
 */

import { partsFromSerial } from '../dates.js';
import {
  VARIADIC,
  arrayOf,
  err,
  isErrCode,
  isError,
  num,
  text,
  toNumber,
  toText,
  type FnSpec,
  type Scalar,
} from '../values.js';
import {
  boolArg,
  collectScalars,
  intArg,
  isFailure,
  optIntArg,
  scalarArg,
  textArg,
  wildcardMatch,
} from './helpers.js';

/** Pads a number with leading zeros to `width` digits. */
function pad(value: number, width: number): string {
  return String(Math.abs(Math.trunc(value))).padStart(width, '0');
}

/** Groups the integer part with thousands separators. */
function withThousands(digits: string): string {
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return out;
}

/** True when a format string looks like a date/time format rather than a number. */
function isDateFormat(format: string): boolean {
  return /[ydhs]/i.test(format) && !/[#0]/.test(format);
}

/** Renders a serial date through the supported date and time tokens. */
function formatDate(serial: number, format: string): string {
  const p = partsFromSerial(serial);
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const tokens: [string, string][] = [
    ['yyyy', String(p.year).padStart(4, '0')],
    ['yy', pad(p.year % 100, 2)],
    ['mmmm', MONTH_NAMES[p.month - 1] as string],
    ['mmm', (MONTH_NAMES[p.month - 1] as string).slice(0, 3)],
    ['mm', pad(p.month, 2)],
    ['dddd', DAY_NAMES[p.weekday] as string],
    ['ddd', (DAY_NAMES[p.weekday] as string).slice(0, 3)],
    ['dd', pad(p.day, 2)],
    ['hh', pad(p.hour, 2)],
    ['ss', pad(p.second, 2)],
    ['am/pm', p.hour < 12 ? 'AM' : 'PM'],
    ['h', String(hour12)],
    ['m', String(p.month)],
    ['d', String(p.day)],
    ['s', String(p.second)],
  ];
  let out = '';
  let i = 0;
  const lower = format.toLowerCase();
  while (i < lower.length) {
    // `nn` after an hour token means minutes; Excel disambiguates `m` by
    // context, and this subset uses `nn`/`n` for minutes to stay unambiguous.
    if (lower.startsWith('nn', i)) {
      out += pad(p.minute, 2);
      i += 2;
      continue;
    }
    if (lower[i] === 'n') {
      out += String(p.minute);
      i += 1;
      continue;
    }
    const hit = tokens.find(([token]) => lower.startsWith(token, i));
    if (hit === undefined) {
      out += format[i] as string;
      i += 1;
      continue;
    }
    out += hit[1];
    i += hit[0].length;
  }
  return out;
}

const MONTH_NAMES: readonly string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_NAMES: readonly string[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Renders a number through a numeric format: `0` and `#` digit placeholders, a
 * `.` decimal point, `,` thousands grouping, and a trailing `%` that scales by
 * 100. Anything else in the format string is emitted literally.
 */
function formatNumber(value: number, format: string): string {
  const percent = format.includes('%');
  const scaled = percent ? value * 100 : value;
  const grouped = /[#0],[#0]/.test(format) || format.includes(',#') || format.includes(',0');
  const decimalPart = format.split('.')[1] ?? '';
  const decimals = (decimalPart.match(/[0#]/g) ?? []).length;
  const fixed = Math.abs(scaled).toFixed(decimals);
  const [intPart = '0', fracPart = ''] = fixed.split('.');
  const intText = grouped ? withThousands(intPart) : intPart;
  const sign = scaled < 0 ? '-' : '';
  const body = decimals > 0 ? `${intText}.${fracPart}` : intText;
  return `${sign}${body}${percent ? '%' : ''}`;
}

/** `LEFT` and `RIGHT` differ only in which end they take. */
function edgeFn(name: 'LEFT' | 'RIGHT'): FnSpec {
  return {
    name,
    min: 1,
    max: 2,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      const count = optIntArg(args, 1, ctx, 1);
      if (isFailure(count)) return count.fail;
      if (count < 0) return err('#VALUE!');
      const chars = [...source];
      const taken = name === 'LEFT' ? chars.slice(0, count) : chars.slice(Math.max(0, chars.length - count));
      return text(taken.join(''));
    },
  };
}

/** Title-cases every run of letters, as `PROPER` does. */
function properCase(source: string): string {
  let out = '';
  let inWord = false;
  for (const ch of source) {
    const isLetter = /\p{L}|\p{N}/u.test(ch);
    out += inWord ? ch.toLowerCase() : ch.toUpperCase();
    inWord = isLetter;
  }
  return out;
}

export const TEXT_FUNCTIONS: readonly FnSpec[] = [
  edgeFn('LEFT'),
  edgeFn('RIGHT'),
  {
    name: 'MID',
    min: 3,
    max: 3,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      const start = intArg(args, 1, ctx);
      if (isFailure(start)) return start.fail;
      const count = intArg(args, 2, ctx);
      if (isFailure(count)) return count.fail;
      if (start < 1 || count < 0) return err('#VALUE!');
      return text([...source].slice(start - 1, start - 1 + count).join(''));
    },
  },
  {
    name: 'LEN',
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      return isFailure(source) ? source.fail : num([...source].length);
    },
  },
  {
    name: 'TRIM',
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      return text(source.trim().replace(/ {2,}/g, ' '));
    },
  },
  {
    name: 'UPPER',
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      return isFailure(source) ? source.fail : text(source.toUpperCase());
    },
  },
  {
    name: 'LOWER',
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      return isFailure(source) ? source.fail : text(source.toLowerCase());
    },
  },
  {
    name: 'PROPER',
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      return isFailure(source) ? source.fail : text(properCase(source));
    },
  },
  {
    name: 'CONCAT',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      let out = '';
      for (const cell of collectScalars(args, ctx)) {
        if (isError(cell)) return cell;
        const t = toText(cell);
        if (isErrCode(t)) return err(t);
        out += t;
      }
      return text(out);
    },
  },
  {
    name: 'TEXTJOIN',
    min: 3,
    max: VARIADIC,
    fn: (args, ctx) => {
      const delimiter = textArg(args, 0, ctx);
      if (isFailure(delimiter)) return delimiter.fail;
      const ignoreEmpty = boolArg(args, 1, ctx);
      if (isFailure(ignoreEmpty)) return ignoreEmpty.fail;
      const parts: string[] = [];
      for (const cell of collectScalars(args, ctx, 2)) {
        if (isError(cell)) return cell;
        const t = toText(cell);
        if (isErrCode(t)) return err(t);
        if (ignoreEmpty && t.length === 0) continue;
        parts.push(t);
      }
      return text(parts.join(delimiter));
    },
  },
  {
    name: 'TEXT',
    min: 2,
    max: 2,
    fn: (args, ctx) => {
      const value = scalarArg(args, 0, ctx);
      if (isError(value)) return value;
      const format = textArg(args, 1, ctx);
      if (isFailure(format)) return format.fail;
      const folded = format.trim();
      if (folded.length === 0 || folded.toLowerCase() === 'general') {
        const t = toText(value);
        return isErrCode(t) ? err(t) : text(t);
      }
      if (folded === '@') {
        const t = toText(value);
        return isErrCode(t) ? err(t) : text(t);
      }
      const n = toNumber(value);
      if (isErrCode(n)) {
        // Non-numeric input passes through unchanged, as Excel does for text.
        const t = toText(value);
        return isErrCode(t) ? err(t) : text(t);
      }
      return text(isDateFormat(folded) ? formatDate(n, folded) : formatNumber(n, folded));
    },
  },
  {
    name: 'VALUE',
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const value = scalarArg(args, 0, ctx);
      if (isError(value)) return value;
      const n = toNumber(value);
      return isErrCode(n) ? err(n) : num(n);
    },
  },
  {
    name: 'SUBSTITUTE',
    min: 3,
    max: 4,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      const target = textArg(args, 1, ctx);
      if (isFailure(target)) return target.fail;
      const replacement = textArg(args, 2, ctx);
      if (isFailure(replacement)) return replacement.fail;
      if (target.length === 0) return text(source);
      if (args[3] === undefined) return text(source.split(target).join(replacement));
      const instance = intArg(args, 3, ctx);
      if (isFailure(instance)) return instance.fail;
      if (instance < 1) return err('#VALUE!');
      let seen = 0;
      let from = 0;
      for (;;) {
        const at = source.indexOf(target, from);
        if (at === -1) return text(source);
        seen += 1;
        if (seen === instance) {
          return text(source.slice(0, at) + replacement + source.slice(at + target.length));
        }
        from = at + target.length;
      }
    },
  },
  {
    name: 'REPLACE',
    min: 4,
    max: 4,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      const start = intArg(args, 1, ctx);
      if (isFailure(start)) return start.fail;
      const count = intArg(args, 2, ctx);
      if (isFailure(count)) return count.fail;
      const replacement = textArg(args, 3, ctx);
      if (isFailure(replacement)) return replacement.fail;
      if (start < 1 || count < 0) return err('#VALUE!');
      const chars = [...source];
      const head = chars.slice(0, start - 1).join('');
      const tail = chars.slice(start - 1 + count).join('');
      return text(head + replacement + tail);
    },
  },
  {
    name: 'FIND',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const needle = textArg(args, 0, ctx);
      if (isFailure(needle)) return needle.fail;
      const haystack = textArg(args, 1, ctx);
      if (isFailure(haystack)) return haystack.fail;
      const start = optIntArg(args, 2, ctx, 1);
      if (isFailure(start)) return start.fail;
      if (start < 1 || start > haystack.length + 1) return err('#VALUE!');
      const at = haystack.indexOf(needle, start - 1);
      return at === -1 ? err('#VALUE!') : num(at + 1);
    },
  },
  {
    name: 'SEARCH',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const needle = textArg(args, 0, ctx);
      if (isFailure(needle)) return needle.fail;
      const haystack = textArg(args, 1, ctx);
      if (isFailure(haystack)) return haystack.fail;
      const start = optIntArg(args, 2, ctx, 1);
      if (isFailure(start)) return start.fail;
      if (start < 1 || start > haystack.length + 1) return err('#VALUE!');
      const scope = haystack.slice(start - 1);
      if (/[*?]/.test(needle)) {
        // Wildcard search: the first position where the pattern matches a prefix.
        for (let i = 0; i < scope.length + 1; i++) {
          for (let len = 0; len + i <= scope.length; len++) {
            if (wildcardMatch(scope.slice(i, i + len), needle)) return num(start + i);
          }
        }
        return err('#VALUE!');
      }
      const at = scope.toUpperCase().indexOf(needle.toUpperCase());
      return at === -1 ? err('#VALUE!') : num(start + at);
    },
  },
  {
    name: 'SPLIT',
    min: 2,
    max: 2,
    array: true,
    fn: (args, ctx) => {
      const source = textArg(args, 0, ctx);
      if (isFailure(source)) return source.fail;
      const delimiter = textArg(args, 1, ctx);
      if (isFailure(delimiter)) return delimiter.fail;
      const parts = delimiter.length === 0 ? [...source] : source.split(delimiter);
      const row: Scalar[] = parts.map((part) => text(part));
      return arrayOf([row]);
    },
  },
];

/** Exported for the unit tests, which check the format subset directly. */
export const __testables = { formatNumber, formatDate, properCase };
